import React, { useState } from "react";
import { camelCase, sortBy } from "lodash";

import { ArrowDropDown } from "@material-ui/icons";
import { FactorCard, Link, StyledGrid } from "components/simple";
import { color, spacingDefaults } from "style/constants";

import FactorCardHeader from "components/simple/FactorCard/FactorCardHeader";
import OverviewCard from "components/simple/OverviewCard";
import { StyledP } from "components/pages/Settings/styled";
import { useParams } from "react-router-dom";
import { NewCycle } from "models/userAssessmentCycle";
import { PillarName, Scores, ScoreType, UserAssessmentsScore } from "models/score";
import { FactorInfo } from "models/bhi";
import { SelectedSubFactor } from "../../types";
import { getNewPillarScore } from "../..";

interface OverviewProps {
  cycles: NewCycle[];
  selectedCycleIndex: number;
  selectedFactor?: PillarName;
  factorInfo: FactorInfo[];
}

export const getSubFactorScores = (
  cycle: NewCycle,
  subFactorList: string[],
  selectedFactor?: PillarName
) => {
  const subFactorData = sortBy(
    cycle.bhiRefactorPillarScores.find(pillar => pillar.pillarName === selectedFactor)
      ?.bhiRefactorUserAssessments,
    [x => x.name]
  );

  const filteredSubFactorData: UserAssessmentsScore[] = [];
  subFactorData.map(sf => {
    switch (sf.name) {
      case "Mood":
        // stress / anxiety / depression is nested in Mood scores array
        sf.scores.map((s: Scores) => {
          if (
            s.scoreName &&
            s.scoreType === ScoreType.PERCENTILE &&
            subFactorList.find(x => x.toLowerCase() === s.scoreName.toLowerCase())
          ) {
            const capitalize = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || "";

            filteredSubFactorData.push({
              name: capitalize(s.scoreName),
              scores: [s]
            });
          }
        });
        break;
      case "Strategy":
      // No special handling needed as 'Aggregate' score result is
      // also the only singular 'percentile' score.  Fall through to default case.
      default:
        if (sf.name === subFactorList.find(x => x === sf.name)) {
          filteredSubFactorData.push(sf);
        }
        break;
    }
  });
  filteredSubFactorData.sort((a, b) => a.name.localeCompare(b.name));

  const scoresArr = filteredSubFactorData?.map(item => item.scores);

  // return average of PERCENTILE scores for
  // each sub factor
  const percentileScoreTotals =
    scoresArr?.flatMap(item => {
      const percentiles = item?.filter(x => x.scoreType === ScoreType.PERCENTILE);
      return percentiles.reduce((acc, curr) => acc + curr.value, 0) / percentiles.length;
    }) || [];

  return percentileScoreTotals;
};

const Overview: React.FC<OverviewProps> = ({
  cycles,
  selectedCycleIndex,
  selectedFactor,
  factorInfo
}) => {
  const { id: userId } = useParams<{ id: string }>();
  const [selectedSubFactor, setSelectedSubFactor] = useState<SelectedSubFactor>({
    name: "Overall",
    description: "Toggle through to see additional details",
    index: 0
  });

  const currentCycle = cycles[selectedCycleIndex];
  const indexScore =
    currentCycle.bhiRefactorPillarScores.find(ps => ps.pillarName === "newIndex")?.value ?? 0;

  const connectedness = getNewPillarScore(currentCycle, PillarName.CONNECTEDNESS);
  const clarity = getNewPillarScore(currentCycle, PillarName.CLARITY);
  const emotionalBalance = getNewPillarScore(currentCycle, PillarName.EMOTIONAL_BALANCE);

  const selectedFactorInfo = factorInfo.find(item => camelCase(item.name) === selectedFactor);

  const subFactorList = selectedFactorInfo?.subFactors?.map(subFactor => subFactor.name) || [];

  /* TODO: update this function to return an array of objects with factor name and value
     that way we don't have to worry about returning this array in the right order for the tree
  */
  const getScores = () => {
    if (selectedFactor) return getSubFactorScores(currentCycle, subFactorList, selectedFactor);
    else return [emotionalBalance, connectedness, clarity];
  };

  const getRadarLabels = () => {
    if (selectedFactor) {
      return subFactorList;
    } else return ["Emotional Balance", "Connectedness", "Clarity"];
  };

  const getFactorScore = (factor: PillarName | string): number => {
    switch (factor) {
      case PillarName.EMOTIONAL_BALANCE:
        return emotionalBalance;
      case PillarName.CONNECTEDNESS:
        return connectedness;
      case PillarName.CLARITY:
        return clarity;
      default:
        return 0;
    }
  };

  const subFactors = {
    name: selectedFactorInfo?.name || "",
    description: selectedFactorInfo?.description || "",
    subFactors: [
      {
        name: "Overall",
        description: "Toggle through to see additional details"
      },
      ...(sortBy(selectedFactorInfo?.subFactors, ["name"]) || [])
    ]
  };

  return (
    <StyledGrid
      alignItems="center"
      item
      container
      justifyContent="space-evenly"
      style={{ rowGap: "2rem" }}
    >
      <StyledGrid item xs={12}>
        {selectedFactor ? (
          <StyledGrid padding={`0 ${spacingDefaults.xsmall}`}>
            <FactorCardHeader
              score={getFactorScore(selectedFactor)}
              selectedFactor={selectedFactor}
            />
            <FactorCard
              factorInfo={subFactors}
              selectedFactor={selectedFactor}
              selectedSubFactor={selectedSubFactor}
              setSelectedSubFactor={setSelectedSubFactor}
              subFactorScores={getScores()}
            />
          </StyledGrid>
        ) : (
          <StyledGrid padding={spacingDefaults.xsmall}>
            <StyledGrid container justifyContent="flex-end">
              <Link
                to={{
                  pathname: `/pdf/${userId}`,
                  search: `?cycle=${selectedCycleIndex}`
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledGrid alignItems="center" container itemMargin="5px">
                  <ArrowDropDown />
                  <StyledP color={color.BLACK}>Download Latest Report</StyledP>
                </StyledGrid>
              </Link>
            </StyledGrid>
            <OverviewCard
              cycles={cycles}
              labels={getRadarLabels()}
              scores={getScores()}
              selectedCycleIndex={selectedCycleIndex}
              selectedFactor={selectedFactor}
            />
          </StyledGrid>
        )}
      </StyledGrid>
    </StyledGrid>
  );
};

export default Overview;
